import type { GameMap, Player, Round, UtilityCategory } from "../api/types";

/** World to minimap projection. The axes are swapped: the game's X runs down
 *  the image and its Y across it. */
export function project(
  map: GameMap,
  gameX: number,
  gameY: number,
  width: number,
  height: number,
): [number, number] {
  return [
    (gameY * map.xMultiplier + map.xScalarToAdd) * width,
    (gameX * map.yMultiplier + map.yScalarToAdd) * height,
  ];
}

/** Minimap back to world. The inverse of project, for turning where somebody
 *  clicked into the ground they clicked on. */
export function unproject(
  map: GameMap,
  screenX: number,
  screenY: number,
  width: number,
  height: number,
): [number, number] {
  return [
    (screenY / height - map.yScalarToAdd) / map.yMultiplier,
    (screenX / width - map.xScalarToAdd) / map.xMultiplier,
  ];
}

/** The nearest named region, for labelling an event. */
export function calloutAt(map: GameMap, x: number, y: number): string {
  let best = "";
  let bestDistance = Infinity;
  for (const c of map.callouts) {
    const d = (c.x - x) ** 2 + (c.y - y) ** 2;
    if (d < bestDistance) {
      bestDistance = d;
      best = c.superRegion ? `${c.superRegion} ${c.regionName}` : c.regionName;
    }
  }
  return best;
}

export const ATTACK_COLOR = "#ff5555";
export const DEFENSE_COLOR = "#3ddbc6";

/** Sides are per half, so a team's colour depends on the round being watched. */
export function sideColor(player: Player, round: Round | undefined): string {
  if (!round?.attackers) return DEFENSE_COLOR;
  return player.team === round.attackers ? ATTACK_COLOR : DEFENSE_COLOR;
}

export function isAttacker(player: Player, round: Round | undefined): boolean {
  return !!round?.attackers && player.team === round.attackers;
}

export const CATEGORY_COLOR: Record<UtilityCategory, string> = {
  smoke: "#c9d1e0",
  wall: "#ffb347",
  flash: "#fff27a",
  trap: "#ff7ad9",
  recon: "#7ad4ff",
  damage: "#ff6b3d",
  mobility: "#9b8cff",
  other: "#9aa4b5",
};

export const CATEGORY_LABEL: Record<UtilityCategory, string> = {
  smoke: "fumée",
  wall: "mur",
  flash: "flash",
  trap: "piège",
  recon: "reconnaissance",
  damage: "dégâts",
  mobility: "mobilité",
  other: "autre",
};

/** Round clock, counting down the way the game does. */
export function roundClock(round: Round, timeMs: number): string {
  const from = round.liveMs ?? round.buyMs ?? round.startMs;
  const elapsed = Math.max(0, timeMs - from);
  return formatDuration(elapsed);
}

export function formatDuration(ms: number): string {
  const total = Math.max(0, Math.round(ms / 1000));
  const minutes = Math.floor(total / 60);
  const seconds = total % 60;
  return `${minutes}:${seconds.toString().padStart(2, "0")}`;
}

export function formatBytes(bytes: number): string {
  if (bytes >= 1 << 30) return `${(bytes / (1 << 30)).toFixed(1)} Go`;
  if (bytes >= 1 << 20) return `${Math.round(bytes / (1 << 20))} Mo`;
  return `${Math.round(bytes / 1024)} Ko`;
}

export function formatDate(iso: string | undefined): string {
  if (!iso) return "";
  return new Date(iso).toLocaleString("fr-FR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

/** How long until a share link lapses. */
export function expiresIn(iso: string): string {
  const ms = new Date(iso).getTime() - Date.now();
  if (ms <= 0) return "expiré";
  const hours = Math.floor(ms / 3_600_000);
  if (hours >= 24) return `${Math.floor(hours / 24)} j`;
  if (hours >= 1) return `${hours} h`;
  return `${Math.max(1, Math.floor(ms / 60_000))} min`;
}

export const OUTCOME_LABEL: Record<string, string> = {
  defuse: "désamorçage",
  detonate: "explosion",
  elimination: "élimination",
  timeout: "temps écoulé",
  unknown: "indéterminé",
};
